<template>
  <div id="stagingDashboard" v-if="inventory && company">
    <StagingSelect ref="staging-select" :inventory="inventory"/>
    <br/>
    <br/>

    <Datatable ref="datatable" entity="inventory_staging" :parameters="{inventory}"
      :global-actions="[{text: 'inventory_staging.create', method: editModal}]"
      :row-actions="[
        {method: editModal,           condition: r => r.status === 'draft', text: 'edit' },
        {method: deleteModal,         condition: r => r.status === 'draft', text: 'delete' },
        {method: deployPreviewModal,  condition: r => r.status === 'draft', text: 'inventory_staging.deploy_preview' },
        {method: deployModal,         condition: r => r.status === 'draft', text: 'deploy' },

        {method: deploymentChangesModal, condition: r => r.status === 'deployed', text: 'inventory_staging.deployment_view' },
        {method: rollbackPreviewModal,   condition: r => r.status === 'deployed', text: 'inventory_staging.rollback_preview' },
        {method: rollbackModal,          condition: r => r.status === 'deployed', text: 'rollback' }
      ]"
    />

    <Modal name="staging-edit"
      :actions="[{name: $t('cancel'), action: 'close'}, {name: $t('save'), primary: true, action: () => saveStaging(this.editForm)}]">
      <div class="group">
        <label>{{$t('inventory_staging.description')}} ({{editForm.name ? editForm.name.length : 0}}/{{nameMaxLength}})</label>
        <input type="text" v-model="editForm.name" :maxlength="nameMaxLength">
      </div>
    </Modal>

    <Modal name="staging-diffs-view" :title="$t(previewData.modalTitleKey)" :width="800"
           :actions="[{name: $t('back'), action: 'close'}]">
      <StagingDiffs v-if="previewData.id" :staging="previewData"/>
    </Modal>

    <Modal name="staging-delete" :title="$t('inventory_staging.delete_confirm_title')"
      :actions="[{name: $t('cancel'), action: 'close'}, {name: $t('confirm'), primary: true, action: () => deleteStaging(this.deleteForm)}]">
      <div class="confirmation">
        <div>{{$t('inventory_staging.delete_confirm_txt')}}</div>
        <div><b>{{deleteForm.name}}</b></div>
      </div>
    </Modal>

    <Modal name="staging-rollback"
      :actions="[{name: $t('cancel'), action: 'close'}, {name: $t('confirm'), primary: true, action: () => rollbackStaging(this.rollbackForm)}]">
      <div class="confirmation">
        <div>{{$t('inventory_staging.rollback_confirm_txt')}}</div>
        <div><b>{{rollbackForm.name}}</b></div>
      </div>
    </Modal>

    <Modal name="staging-deploy"
      :actions="[{name: $t('back'), action: 'close'}, {name: $t('confirm'), primary: true, action: () => deployStaging(this.deployForm)}]">
      <div class="confirmation">
        <div>{{$t('inventory_staging.deploy_confirm_txt')}}</div>
        <div><b>{{deployForm.name}}</b></div>
      </div>
    </Modal>
  </div>
</template>

<script>
import Datatable from "@/components/Datatable.vue";
import StagingDiffs from "@/components/inventory/StagingDiffs.vue";
import StagingSelect from "@/components/inventory/StagingSelect.vue";
import {Core} from "@/core/Core";

export default {
  name: "StagingDashboard",

  components: {StagingDiffs, Datatable, StagingSelect},

  data() {
    return {
      nameMaxLength: 25,
      editForm: {
        id: null,
        name: null,
      },
      deleteForm: {
        id: null,
        name: null,
      },
      deployForm: {
        id: null,
        name: null,
      },
      rollbackForm: {
        id: null,
        name: null,
      },
      previewData: {
        id: null,
        changes: [],
        modalTitleKey: ''
      }
    }
  },

  computed: {
    contextInventory() {
      return this.$store.state.contextInventory;
    },
    company() {
      return this.$store.state.contextCompany;
    },
    inventory() {
      return this.contextInventory ? this.contextInventory.id : null;
    },
  },

  methods: {
    async editModal(row = {}) {
      this.editForm.id = row.id;
      this.editForm.name = this.rowName(row);
      this.$modal.show("staging-edit");
    },

    async deleteModal(row) {
      this.deleteForm.id = row.id;
      this.deleteForm.name = this.rowName(row);
      this.$modal.show("staging-delete");
    },

    async deployModal(row) {
      this.deployForm.id = row.id;
      this.deployForm.name = this.rowName(row);
      this.$modal.show("staging-deploy");
    },

    async rollbackModal(row) {
      this.rollbackForm.id = row.id;
      this.rollbackForm.name = this.rowName(row);
      this.$modal.show("staging-rollback");
    },

    async saveStaging(data) {
      let name = data.name
      if (!name || !name.trim()) {
        toast(this.$t('inventory_staging.name_required'), 'error')
        return;
      }
      this.$modal.hide("staging-edit");
      let isCreation = !data.id
      let response = isCreation
          ? await Core.noThrowRequest('POST', '/api/inventory_stagings', {...data, inventory: this.inventory})
          : await Core.noThrowRequest('PUT', `/api/inventory_stagings/${data.id}`, data);

      if (response) {
        let msg = isCreation ? this.$t('inventory_staging.on_created') : this.$t('inventory_staging.on_saved');
        msg = msg.replace("%NAME%", name)
        toast(msg, 'success');
        await this.onAnyModification();
      }
    },

    async deleteStaging(data) {
      this.$modal.hide("staging-delete");
      let response = await Core.noThrowRequest('DELETE', `/api/inventory_stagings/${data.id}`);
      if (response) {
        let msg = this.$t('inventory_staging.on_deleted').replace("%NAME%", data.name);
        toast(msg, 'success');
        await this.onAnyModification();
      }
    },

    async deployStaging(data) {
      this.$modal.hide("staging-deploy");
      let response = await Core.noThrowRequest('POST', `/api/inventory_stagings/${data.id}/apply`);
      if (response) {
        let msg = this.$t('inventory_staging.on_deployed').replace("%NAME%", data.name);
        toast(msg, 'success');
        await this.onAnyModification();
      }
    },

    async rollbackStaging(data) {
      this.$modal.hide("staging-rollback");
      let response = await Core.noThrowRequest('POST', `/api/inventory_stagings/${data.id}/rollback`);
      if (response) {
        let msg = this.$t('inventory_staging.on_rollback').replace("%NAME%", data.name);
        toast(msg, 'success');
        await this.onAnyModification();
      }
    },

    async deployPreviewModal(row) {
      let response = await Core.noThrowRequest('GET', `/api/inventory_stagings/${row.id}/preview_diffs`);
      if (response) {
        this.previewData = response;
        this.previewData.modalTitleKey = 'inventory_staging.deploy_preview';
        this.$modal.show("staging-diffs-view");
      }
    },

    async rollbackPreviewModal(row) {
      let response = await Core.noThrowRequest('GET', `/api/inventory_stagings/${row.id}/preview_diffs?rollback=1`);
      if (response) {
        this.previewData = response;
        this.previewData.modalTitleKey = 'inventory_staging.rollback_preview';
        this.$modal.show("staging-diffs-view");
      }
    },

    async deploymentChangesModal(row) {
      let response = await Core.noThrowRequest('GET', `/api/inventory_stagings/${row.id}/deployment_diffs`);
      if (response) {
        this.previewData = response;
        this.previewData.modalTitleKey = 'inventory_staging.deployment_view';
        this.$modal.show("staging-diffs-view");
      }
    },

    async onAnyModification() {
      await this.$refs.datatable.query();
      this.$store.commit('notifyVersionChange');
    },

    rowName(row) {
      return row.versionName || row.name || ""
    }
  }
}
</script>

<style lang="scss" scoped>
.confirmation {
  text-align: center;

  > :not(:last-child) {
    margin-bottom: 10px;
  }
}
</style>